import { useEffect, useState, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import { AppDispatch } from "@/app/store";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { trackPodcastFiltersChanged } from "@/modules/hub/features/Podcasts/analytics/podcastsAnalytics";
import {
  fetchResources,
  fetchMoreResources,
  setResources,
  setPage,
  setHasMore,
  setScrollPosition,
  setLastFetchParams,
  setSearchInput,
  setShowOnlyFavorites,
  setShowOnlyFeatured,
  setSelectedTrades,
  setSelectedResourceCategories,
  setSelectedBusinessRoles,
  setIsRestoringState,
  clearFilters,
} from "../slices/podcastsSlice";
import { getPodcastsMetadataAction } from "../slices/podcastsMetadataSlice";
import {
  PodcastsFilterFormData,
  PodcastsFilterFormSchema,
} from "./PodcastsFilterFormSchema";
import {
  Trade,
  BusinessRole,
} from "@/modules/hub/features/ResourceLibrary/api/getResourceLibratyMetadata/types";
import { ResourceCategory } from "@/api/fetchCreateUpdateResourceMetadata/types";

export function usePodcasts() {
  const dispatch = useDispatch<AppDispatch>();

  const {
    resources,
    page,
    hasMore,
    lastFetchParams,
    scrollPosition,
    searchInput,
    showOnlyFavorites,
    showOnlyFeatured,
    selectedTrades,
    selectedResourceCategories,
    selectedBusinessRoles,
  } = useSelector((state: RootState) => state.podcasts);

  const { metadata, loadingGet } = useSelector(
    (state: RootState) => state.podcastsMetadata,
  );

  const form = useForm<PodcastsFilterFormData>({
    resolver: zodResolver(PodcastsFilterFormSchema),
    defaultValues: {
      searchTerm: searchInput || "",
      isFavoriteOnly: showOnlyFavorites || false,
      isFeaturedOnly: showOnlyFeatured || false,
      trades: selectedTrades || [],
      categories: selectedResourceCategories || [],
      businessRoles: selectedBusinessRoles || [],
    },
    mode: "onChange",
  });

  const debouncedSearchTerm = useDebouncedValue(
    form.watch("searchTerm") ?? "",
    500,
  );
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const hasTrackedInitialFilters = useRef(false);

  useEffect(() => {
    const subscription = form.watch((values) => {
      dispatch(setSearchInput(values.searchTerm || ""));
      dispatch(setShowOnlyFavorites(values.isFavoriteOnly || false));
      dispatch(setShowOnlyFeatured(values.isFeaturedOnly || false));
      dispatch(
        setSelectedTrades((values.trades ?? []).filter((t): t is Trade => !!t)),
      );
      dispatch(
        setSelectedResourceCategories(
          (values.categories ?? []).filter((c): c is ResourceCategory => !!c),
        ),
      );
      dispatch(
        setSelectedBusinessRoles(
          (values.businessRoles ?? []).filter((e): e is BusinessRole => !!e),
        ),
      );
    });
    return () => subscription.unsubscribe();
  }, [form, dispatch]);

  const buildRequestData = useCallback(() => {
    const values = form.getValues();

    const podcastType = metadata?.resourceTypes?.find(
      (type) => type.name === "Podcast",
    );

    return {
      searchTerm: debouncedSearchTerm?.trim() || undefined,
      showFavorites: values.isFavoriteOnly || undefined,
      showFeatured: values.isFeaturedOnly || undefined,
      tradeIds: values.trades?.map((t) => t.id),
      // Always filter by Podcast resource type
      resourceTypeIds: podcastType ? [podcastType.id] : undefined,
      categoryIds: values.categories?.map((c) => c.id),
      businessRoleIds: values.businessRoles?.map((e) => e.id),
      page: 1,
      pageSize: 10,
    };
  }, [form, debouncedSearchTerm, metadata]);

  const isStateRestorable = useCallback(() => {
    if (!lastFetchParams) return false;

    const getFiltersWithoutPagination = (params: Record<string, unknown>) => {
      const paginationKeys = ["page", "pageSize"];
      return Object.fromEntries(
        Object.entries(params).filter(([key]) => !paginationKeys.includes(key)),
      );
    };

    const currentFilters = getFiltersWithoutPagination(buildRequestData());
    const previousFilters = getFiltersWithoutPagination(lastFetchParams);

    return JSON.stringify(currentFilters) === JSON.stringify(previousFilters);
  }, [lastFetchParams, buildRequestData]);

  useEffect(() => {
    dispatch(getPodcastsMetadataAction());
  }, [dispatch]);

  useEffect(() => {
    if (!isInitialLoading && scrollPosition > 0) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPosition, behavior: "auto" });
      });
    }
  }, [isInitialLoading, scrollPosition]);

  useEffect(() => {
    const saveScroll = () => {
      dispatch(setScrollPosition(window.scrollY));
    };
    window.addEventListener("beforeunload", saveScroll);
    window.addEventListener("pagehide", saveScroll);
    return () => {
      saveScroll();
      window.removeEventListener("beforeunload", saveScroll);
      window.removeEventListener("pagehide", saveScroll);
    };
  }, [dispatch]);

  useEffect(() => {
    const load = async () => {
      // Wait for metadata to load first
      if (!metadata) {
        return;
      }

      if (resources.length > 0 && lastFetchParams && isStateRestorable()) {
        dispatch(setIsRestoringState(true));
        setIsInitialLoading(false);
        return;
      }

      const request = buildRequestData();
      setLoading(true);
      dispatch(setResources([]));
      dispatch(setPage(1));
      dispatch(setHasMore(true));

      await dispatch(fetchResources(request));

      dispatch(setLastFetchParams(request));
      setLoading(false);
      setIsInitialLoading(false);
    };
    load();
  }, [metadata]);

  useEffect(() => {
    if (isInitialLoading || !metadata) return;
    const request = buildRequestData();
    setLoading(true);
    dispatch(setResources([]));
    dispatch(setPage(1));
    dispatch(setHasMore(true));
    dispatch(fetchResources(request));
    dispatch(setLastFetchParams(request));
    setLoading(false);
  }, [
    debouncedSearchTerm,
    form.watch("isFavoriteOnly"),
    form.watch("isFeaturedOnly"),
    form.watch("trades"),
    form.watch("categories"),
    form.watch("businessRoles"),
  ]);

  useEffect(() => {
    if (isInitialLoading) return;

    if (!hasTrackedInitialFilters.current) {
      hasTrackedInitialFilters.current = true;
      return;
    }

    trackPodcastFiltersChanged();
  }, [
    debouncedSearchTerm,
    form.watch("isFavoriteOnly"),
    form.watch("isFeaturedOnly"),
    form.watch("trades"),
    form.watch("categories"),
    form.watch("businessRoles"),
    isInitialLoading,
  ]);

  const fetchMore = useCallback(() => {
    const nextPage = page + 1;
    const request = { ...buildRequestData(), page: nextPage };
    dispatch(setPage(nextPage));
    dispatch(fetchMoreResources(request));
  }, [page, dispatch, buildRequestData]);

  const handleClearFilters = () => {
    dispatch(clearFilters());

    form.reset({
      searchTerm: "",
      isFavoriteOnly: false,
      isFeaturedOnly: false,
      trades: [],
      categories: [],
      businessRoles: [],
    });
  };

  const setFeaturedOnly = () => {
    form.setValue("isFeaturedOnly", true);
  };

  const handleViewAllFeatured = () => {
    setFeaturedOnly();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getResourceTypeIds = () => {
    if (!metadata?.resourceTypes) return undefined;
    const podcastType = metadata.resourceTypes.find(
      (type) => type.name === "Podcast",
    );
    return podcastType ? [podcastType.id] : undefined;
  };

  const shouldShowFeaturedSection = () => {
    const values = form.getValues();
    return (
      !values.searchTerm &&
      !values.isFavoriteOnly &&
      !values.isFeaturedOnly &&
      (values.trades || []).length === 0 &&
      (values.categories || []).length === 0 &&
      (values.businessRoles || []).length === 0
    );
  };

  return {
    form,
    page,
    resources,
    hasMore,
    loading,
    isInitialLoading,
    fetchMore,
    handleClearFilters,
    setFeaturedOnly,
    handleViewAllFeatured,
    getResourceTypeIds,
    shouldShowFeaturedSection,
    sidebarMetadata: metadata,
    isMetadataLoading: loadingGet,
  };
}
